import { PhoneIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import DocumentItem from "../../../../../../../documents/components/DocumentItem";
import { LuFile, LuText, LuUsers } from "react-icons/lu";
import useActivityById from "../../hooks/useActivityById";
import { cn } from "../../../../../../../../lib/utils";
import ContactItem from "../../../../../../../contacts/components/ContactItem";
import ResourceViewsComponent from "../../../../../../../../components/ResourceViewsComponent";
import UserOrganizationItem from "../../../../../../../../components/UserOrganizationItem";
import useNav from "../../../../../../../../hooks/useNavigation";
import Location from "../../../../../../../../components/Location";
import ClickableGroupBadge from "../../../../../../../../components/ClickableGroupBadge";

export default function ActivityDetails({
  activityId,
  className,
}: {
  activityId: string;
  className?: string;
}) {
  const navigateWithLog = useNav();

  const {
    data: activityByIdData,
    loading: activityByIdLoading,
    refetch: refetchActivityById,
  } = useActivityById(activityId);

  if (activityByIdLoading) {
    return null;
  }

  const activity = activityByIdData?.activity;

  if (!activity) {
    return (
      <div className="flex flex-col gap-2 bg-dark p-2 rounded-md">
        <div className="text-sm text-primary">Aucune activité trouvée</div>
      </div>
    );
  }

  const hasDescription = !!activity.description;

  const sectionTitle = (title: string, icon: React.ReactNode) => {
    return (
      <div className="flex items-center gap-2 text-secondary">
        {icon}
        <div className="text-sm text-secondary">{title}</div>
      </div>
    );
  };

  const description = () => {
    if (!hasDescription) return null;

    return (
      <div
        className={cn("flex flex-col gap-2 bg-dark p-2 rounded-md", className)}
      >
        {sectionTitle("Description", <LuText className="w-4 h-4" />)}
        <div className="relative mt-1">
          <div className="overflow-y-auto max-h-[100px] pr-1 custom-scrollbar text-sm break-words whitespace-pre-wrap">
            {activity.description}
          </div>
        </div>
      </div>
    );
  };

  const hasParticipants = (activity.activityParticipants?.length ?? 0) > 0;

  const participantsList = () => {
    if (!hasParticipants) return null;

    return (
      <div
        className={cn("flex flex-col gap-2 bg-dark p-2 rounded-md", className)}
      >
        {sectionTitle("Participants", <LuUsers className="w-4 h-4" />)}
        {activity.activityParticipants?.map((participant) => (
          <UserOrganizationItem
            key={participant.id}
            userOrganization={participant.publicUserOrganization}
            onClick={() => {
              navigateWithLog(
                `/collaborators/${participant.publicUserOrganization.id}`
              );
            }}
          />
        ))}
      </div>
    );
  };

  const hasGroups = (activity.activityGroupParticipants?.length ?? 0) > 0;

  const groupsList = () => {
    if (!hasGroups) return null;

    return (
      <div
        className={cn("flex flex-col gap-2 bg-dark p-2 rounded-md", className)}
      >
        {sectionTitle("Groupes", <UserGroupIcon className="w-4 h-4" />)}
        <div className="flex flex-wrap gap-2">
          {activity.activityGroupParticipants?.map((groupParticipant) => (
            <ClickableGroupBadge
              key={groupParticipant.id}
              userGroup={groupParticipant.userGroup}
              projectId={activity.projectId ?? undefined}
              eventId={activity.eventId ?? undefined}
            />
          ))}
        </div>
      </div>
    );
  };

  const hasDocuments = (activity.documents?.length ?? 0) > 0;

  const documentsList = () => {
    if (!hasDocuments) return null;

    return (
      <div
        className={cn("flex flex-col gap-2 bg-dark p-2 rounded-md", className)}
      >
        {sectionTitle("Documents", <LuFile className="w-4 h-4" />)}
        {activity.documents?.map((document) => (
          <DocumentItem key={document.id} document={document} />
        ))}
      </div>
    );
  };

  const hasContacts = (activity.contacts?.length ?? 0) > 0;

  const contactsList = () => {
    if (!hasContacts) return null;

    return (
      <div
        className={cn("flex flex-col gap-2 bg-dark p-2 rounded-md", className)}
      >
        {sectionTitle("Contacts", <PhoneIcon className="w-4 h-4" />)}

        <div className="flex flex-wrap gap-4">
          {activity.contacts?.map((contact) => {
            if (!contact) return null;
            return (
              <ContactItem
                key={contact.id}
                contact={contact}
                onDelete={() => {
                  refetchActivityById();
                }}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const hasResourceViews = (activity.resourceViews?.length ?? 0) > 0;

  return (
    <div className="flex flex-col gap-2">
      {hasResourceViews && (
        <ResourceViewsComponent
          resourceViews={activity.resourceViews ?? []}
          participants={
            activity.activityParticipants
              ?.filter((participant) => !!participant)
              .map((participant) => participant?.publicUserOrganization) ?? []
          }
        />
      )}
      <Location location={activity.activityLocation} />
      {description()}
      {participantsList()}
      {groupsList()}
      {documentsList()}
      {contactsList()}
    </div>
  );
}
